import { useCurrentFrame, useVideoConfig, interpolate, Easing, AbsoluteFill } from "remotion";
import { loadFont as loadInter } from "@remotion/google-fonts/Inter";

const { fontFamily: inter } = loadInter("normal", { weights: ["600", "700", "800"], subsets: ["latin"] });

const TempleSVG: React.FC<{ bellAngle: number }> = ({ bellAngle }) => (
  <svg width="73" height="80" viewBox="0 0 110 120" fill="none" xmlns="http://www.w3.org/2000/svg">
    <rect x="5" y="100" width="100" height="10" rx="2" fill="#C8A96E" stroke="#8B6914" strokeWidth="1" />
    <rect x="18" y="72" width="74" height="30" rx="2" fill="#E8C97E" stroke="#8B6914" strokeWidth="1" />
    <path d="M42 102 L42 82 Q55 72 68 82 L68 102Z" fill="#5C3D1E" />
    <path d="M12 72 L98 72 L90 58 L20 58Z" fill="#D4A853" stroke="#8B6914" strokeWidth="1" />
    <path d="M22 58 L88 58 L80 44 L30 44Z" fill="#E8C97E" stroke="#8B6914" strokeWidth="1" />
    <path d="M32 44 L78 44 L70 32 L40 32Z" fill="#D4A853" stroke="#8B6914" strokeWidth="1" />
    <path d="M40 32 L70 32 L55 8Z" fill="#E8C97E" stroke="#8B6914" strokeWidth="1" />
    <circle cx="55" cy="8" r="4" fill="#FFD700" stroke="#B8860B" strokeWidth="1" />
    <line x1="55" y1="4" x2="55" y2="-4" stroke="#FF4500" strokeWidth="1.5" />
    <path d="M55 -4 L65 -1 L55 2Z" fill="#FF4500" />
    <g transform={`translate(55, 78) rotate(${bellAngle})`} style={{ transformOrigin: "0px -8px" }}>
      <line x1="0" y1="-8" x2="0" y2="0" stroke="#8B6914" strokeWidth="1.5" />
      <path d="M0 0 C0 0 -8 2 -9 12 L9 12 C8 2 0 0 0 0Z" fill="#FFD700" stroke="#B8860B" strokeWidth="1" />
      <ellipse cx="0" cy="12" rx="9" ry="3" fill="#B8860B" />
      <circle cx="0" cy="16" r="2" fill="#8B6914" />
      <ellipse cx="-3" cy="5" rx="1.5" ry="3" fill="rgba(255,255,255,0.35)" transform="rotate(-10 -3 5)" />
    </g>
  </svg>
);

const ThumbUpSVG: React.FC = () => (
  <svg width="12" height="12" viewBox="0 0 64 64" fill="none" xmlns="http://www.w3.org/2000/svg">
    <path d="M20 28L28 8C30 8 34 9 34 16V24H52C54 24 56 26 55 30L50 52C49 54 47 55 45 55H20V28Z" fill="white" stroke="rgba(255,255,255,0.5)" strokeWidth="2" strokeLinejoin="round" />
    <rect x="8" y="28" width="12" height="28" rx="3" fill="white" stroke="rgba(255,255,255,0.5)" strokeWidth="2" />
  </svg>
);

export const DevaKathaLS1: React.FC = () => {
  const frame = useCurrentFrame();
  const { fps } = useVideoConfig();

  const swingDuration = Math.round(2.5 * fps);

  const bellAngle = interpolate(
    frame,
    [0, Math.round(swingDuration * 0.12), Math.round(swingDuration * 0.32), Math.round(swingDuration * 0.52), Math.round(swingDuration * 0.68), Math.round(swingDuration * 0.82), swingDuration],
    [0, 30, -22, 14, -8, 4, 0],
    { extrapolateRight: "clamp", extrapolateLeft: "clamp" }
  );

  const slideY = interpolate(frame, [0, Math.round(0.5 * fps)], [40, 0], {
    extrapolateRight: "clamp",
    extrapolateLeft: "clamp",
    easing: Easing.bezier(0.16, 1, 0.3, 1),
  });

  const fadeIn = interpolate(frame, [0, Math.round(0.4 * fps)], [0, 1], {
    extrapolateRight: "clamp",
    extrapolateLeft: "clamp",
  });

  const btnOpacity = interpolate(frame, [Math.round(0.6 * fps), Math.round(1.2 * fps)], [0, 1], {
    extrapolateRight: "clamp",
    extrapolateLeft: "clamp",
  });

  const fadeOut = interpolate(frame, [Math.round(3.5 * fps), Math.round(4 * fps)], [1, 0], {
    extrapolateLeft: "clamp",
    extrapolateRight: "clamp",
  });

  const likeClick = interpolate(
    frame,
    [Math.round(1.5 * fps), Math.round(1.55 * fps), Math.round(1.7 * fps)],
    [1, 0.88, 1.05],
    { extrapolateRight: "clamp", extrapolateLeft: "clamp" }
  );
  const likeClicked = frame >= Math.round(1.55 * fps);

  const subClick = interpolate(
    frame,
    [Math.round(2.2 * fps), Math.round(2.28 * fps), Math.round(2.4 * fps)],
    [1, 0.88, 1.06],
    { extrapolateRight: "clamp", extrapolateLeft: "clamp" }
  );
  const subClicked = frame >= Math.round(2.28 * fps);

  return (
    <AbsoluteFill style={{ overflow: 'hidden' }}>
      <div style={{ position: 'absolute', inset: 0, opacity: fadeIn * fadeOut, transform: 'scale(1.5)', transformOrigin: 'bottom right' }}>
      <div
        style={{
          position: "absolute",
          bottom: 47,
          right: 40,
          transform: `translateY(${slideY}px)`,
          display: "flex",
          alignItems: "center",
          gap: 11,
          background: "rgba(0,0,0,0.55)",
          backdropFilter: "blur(8px)",
          borderRadius: 13,
          padding: "8px 13px 8px 8px",
          border: "1px solid rgba(255,215,0,0.3)",
          boxShadow: "0 8px 32px rgba(0,0,0,0.5)",
        }}
      >
        <div style={{ filter: "drop-shadow(0 4px 8px rgba(0,0,0,0.5))" }}>
          <TempleSVG bellAngle={bellAngle} />
        </div>

        <div style={{ opacity: btnOpacity, display: "flex", flexDirection: "column", gap: 7 }}>
          <div
            style={{
              display: "flex",
              alignItems: "center",
              gap: 5,
              background: likeClicked ? "#1a73e8" : "rgba(255,255,255,0.15)",
              border: `1px solid ${likeClicked ? "#1a73e8" : "rgba(255,255,255,0.3)"}`,
              borderRadius: 16,
              padding: "5px 12px",
              transform: `scale(${likeClick})`,
              boxShadow: likeClicked ? "0 4px 16px rgba(26,115,232,0.5)" : "none",
            }}
          >
            <ThumbUpSVG />
            <span style={{ color: "#fff", fontSize: 12, fontWeight: 700, fontFamily: inter }}>
              {likeClicked ? "Liked!" : "Like"}
            </span>
          </div>

          <div
            style={{
              display: "flex",
              alignItems: "center",
              gap: 5,
              background: subClicked ? "#606060" : "#FF0000",
              borderRadius: 16,
              padding: "5px 12px",
              transform: `scale(${subClick})`,
              boxShadow: subClicked ? "none" : "0 4px 20px rgba(255,0,0,0.6)",
            }}
          >
            <span style={{ color: "#fff", fontSize: 12, fontWeight: 800, fontFamily: inter }}>
              {subClicked ? "Subscribed ✓" : "Subscribe"}
            </span>
          </div>
        </div>
      </div>
      </div>
    </AbsoluteFill>
  );
};

;
    var _remotion_globalVariableA;
    var _remotion_globalVariableB;
    // Legacy CSS implementations will `eval` browser code in a Node.js context
    // to extract CSS. For backwards compatibility, we need to check we're in a
    // browser context before continuing.
    if (typeof self !== 'undefined' &&
        // AMP / No-JS mode does not inject these helpers:
        '$RefreshHelpers$' in self) {
        const currentExports = __webpack_module__.exports;
        const prevExports = (_remotion_globalVariableB = (_remotion_globalVariableA = __webpack_module__.hot.data) === null || _remotion_globalVariableA === void 0 ? void 0 : _remotion_globalVariableA.prevExports) !== null && _remotion_globalVariableB !== void 0 ? _remotion_globalVariableB : null;
        // This cannot happen in MainTemplate because the exports mismatch between
        // templating and execution.
        self.$RefreshHelpers$.registerExportsForReactRefresh(currentExports, __webpack_module__.id);
        // A module can be accepted automatically based on its exports, e.g. when
        // it is a Refresh Boundary.
        if (self.$RefreshHelpers$.isReactRefreshBoundary(currentExports)) {
            // Save the previous exports on update so we can compare the boundary
            // signatures.
            __webpack_module__.hot.dispose((data) => {
                data.prevExports = currentExports;
            });
            // Unconditionally accept an update to this module, we'll check if it's
            // still a Refresh Boundary later.
            __webpack_module__.hot.accept();
            // This field is set when the previous version of this module was a
            // Refresh Boundary, letting us know we need to check for invalidation or
            // enqueue an update.
            if (prevExports !== null) {
                // A boundary can become ineligible if its exports are incompatible
                // with the previous exports.
                //
                // For example, if you add/remove/change exports, we'll want to
                // re-execute the importing modules, and force those components to
                // re-render. Similarly, if you convert a class component to a
                // function, we want to invalidate the boundary.
                if (self.$RefreshHelpers$.shouldInvalidateReactRefreshBoundary(prevExports, currentExports)) {
                    __webpack_module__.hot.invalidate();
                }
                else {
                    self.$RefreshHelpers$.scheduleUpdate();
                }
            }
        }
        else {
            // Since we just executed the code for the module, it's possible that the
            // new exports made it ineligible for being a boundary.
            // We only care about the case when we were _previously_ a boundary,
            // because we already accepted this update (accidental side effect).
            const isNoLongerABoundary = prevExports !== null;
            if (isNoLongerABoundary) {
                __webpack_module__.hot.invalidate();
            }
        }
    }
